import React, { useState } from 'react';
import { IAuditoriaCreditoEstagnadoResultado, IAuditoriaCreditoEstagnadoItem } from '../interfaces/IAuditoriaCreditoEstagnado';

interface Props {
  resultado: IAuditoriaCreditoEstagnadoResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

// COR-302: o backend agora envia até 200 lançamentos (antes só os 5 últimos) — o frontend
// exibe os 10 mais recentes por padrão e expande sob demanda o restante.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const ItemExpandivel: React.FC<{ item: IAuditoriaCreditoEstagnadoItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);
  const [mostrarTodos, setMostrarTodos] = useState(false);

  return (
    <>
      <tr
        style={{ cursor: 'pointer' }}
        onClick={() => setExpandido(!expandido)}
      >
        <td>
          <span className='fw-bold'>{item.descricaoConta}</span>
          <br />
          <span className='text-muted fs-8'>{item.contaReferencial}</span>
        </td>
        <td className='fw-bold'>{formatCurrency(item.saldoAtual)}</td>
        <td>{formatDate(item.dataUltimaBaixa)}</td>
        <td>
          <span className={`fw-bold ${item.status === 'ERRO' ? 'text-danger' : 'text-warning'}`}>
            {item.mesesInercia} mês(es)
          </span>
        </td>
        <td className='text-danger fw-bold'>{formatCurrency(item.perdaSelic)}</td>
        <td><span className='text-muted fs-8'>{item.taxaSelicAcumuladaPct.toFixed(4)}%</span></td>
        <td><StatusBadge status={item.status} /></td>
        <td className='text-end'>{expandido ? '▲' : '▼'}</td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={8} className='bg-light-secondary p-4'>
            <div className='mb-3'>
              <strong>Diagnóstico:</strong>
              <p className='text-muted mt-1 mb-2'>{item.mensagemDiagnostico}</p>
            </div>

            {item.ultimosLancamentos.length > 0 && (
              <div>
                <strong>Últimos lançamentos da conta:</strong>
                <table className='table table-sm table-row-gray-200 mt-2'>
                  <thead>
                    <tr className='text-muted fw-bold fs-8'>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Valor</th>
                      <th>Histórico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(mostrarTodos ? item.ultimosLancamentos : item.ultimosLancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS)).map((lc, i) => (
                      <tr key={i}>
                        <td>{formatDate(lc.dataLancamento)}</td>
                        <td>
                          <span className={`badge ${lc.tipoLancamento === 'C' ? 'badge-light-success' : 'badge-light-primary'}`}>
                            {lc.tipoLancamento === 'C' ? 'Crédito (Baixa)' : 'Débito (Entrada)'}
                          </span>
                        </td>
                        <td>{formatCurrency(lc.valor)}</td>
                        <td className='text-muted'>{lc.historico || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {item.ultimosLancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
                  <button
                    type='button'
                    className='btn btn-link btn-sm p-0 mt-1'
                    style={{ fontSize: 12 }}
                    onClick={() => setMostrarTodos(true)}
                  >
                    + {item.ultimosLancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
                  </button>
                )}
              </div>
            )}

            {item.status === 'ERRO' && (
              <div className='alert alert-danger mt-3 py-2'>
                <strong>CPC 01 — Teste de Recuperabilidade:</strong> Inércia de {item.mesesInercia} meses
                exige avaliação formal do valor recuperável do ativo. Registrar justificativa no workpaper.
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaCreditoEstagnadoDetalhe: React.FC<Props> = ({ resultado }) => {
  const bannerCor =
    resultado.status === 'ERRO'   ? 'danger' :
    resultado.status === 'ALERTA' ? 'warning' :
    resultado.status === 'AVISO'  ? 'warning' :
                                    'success';

  return (
    <div className='card card-custom card-stretch'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>
            Créditos Fiscais Estagnados — Risco de Impairment
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            Contas 1.01.02.03 (Tributos a Recuperar) e 1.01.02.04 (Tributos Retidos a Compensar)
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.status !== 'CONCILIADO' && (
          <div className={`alert alert-${bannerCor} py-3 mb-5`}>
            <strong>{resultado.status}:</strong> {resultado.mensagemDirecionamento}
          </div>
        )}

        {/* KPIs */}
        <div className='row mb-6'>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-danger'>{resultado.totalErros}</div>
            <div className='text-muted'>Erros (≥ 11 meses)</div>
            <div className='text-muted fs-8'>CPC 01 — Impairment</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted'>Alertas (6–10 meses)</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-1 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted'>Avisos (3–5 meses)</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-1 fw-bolder text-danger'>{formatCurrency(resultado.perdaSelicTotal)}</div>
            <div className='text-muted'>Perda Custo Oportunidade</div>
            <div className='text-muted fs-8'>SELIC Acumulada (BCB 4189)</div>
          </div>
        </div>

        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhum crédito fiscal com inércia superior a 3 meses. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Conta</th>
                  <th>Saldo Atual</th>
                  <th>Última Baixa</th>
                  <th>Inércia</th>
                  <th>Perda SELIC</th>
                  <th>SELIC Acum.</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemExpandivel key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Fundamentação legal */}
        <details className='mt-6'>
          <summary className='text-muted fw-bold cursor-pointer'>Base Legal</summary>
          <p className='text-muted mt-2 fs-7'>{resultado.fundamentacaoLegal}</p>
        </details>
      </div>
    </div>
  );
};

export default AuditoriaCreditoEstagnadoDetalhe;
